import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import IconArrowRight from '../images/IconArrowRight.jsx';
import IconArrowLeft from '../images/IconArrowLeft.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { setError, setErrorReset } from '../../store/error.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import MenuSidebar from '../misc/burger-menu/MenuSidebar.jsx';
import { useLazyGetNotificationsQuery, useMarkNotificationMutation } from '../../api/notifications.js';
import IconBell from '../images/IconBell.jsx';
import { Dialog } from 'primereact/dialog';
import BaseButton from '../form/BaseButton.jsx';
import { link, renderIcon } from '../../utils/helper.js';
import IconCloseFull from '../images/IconCloseFull.jsx';

const Header = ({
    title,
    goBack = true,
    sidebar = true,
    staticHeader = false,
    backRoute,
    forwardRoute,
    forwardPermission,
    forwardPermissionAttribute,
    forwardIcon: ForwardIcon,
    forwardIconClassName
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [notificationsData, setNotificationsData] = useState([]);
    const [show, setShow] = useState(false);
    const [unread, setUnread] = useState(0);
    const { permissions: userPermissions } = useCurrentUser();

    const error = useSelector((state) => state.error);

    const requestParams = useMemo(() => {
        return {
            status: 'unread',
            time: 60,
            per_page: 3
        };
    }, []);

    const [getNotificationsData] = useLazyGetNotificationsQuery({});
    const [markNotification] = useMarkNotificationMutation();

    const handleMarkAsRead = async (ids, status) => {
        if (status === 'read') return;
        try {
            await markNotification({ ids, status: 'read' });
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };
    const backAction = () => {
        if (error.status) {
            if (error.boundary) {
                dispatch(setErrorReset({ reset: true, boundary: true }));
            } else {
                dispatch(setError({ status: false, errorMessages: {} }));
            }
        }
        navigate(backRoute ?? -1);
    };

    const hasForwardPermission = !forwardPermission
        ? true
        : Array.isArray(forwardPermission)
          ? forwardPermission.some((permission) => !!userPermissions[permission]?.[forwardPermissionAttribute])
          : !!userPermissions[forwardPermission]?.[forwardPermissionAttribute];

    useEffect(() => {
        if (location.pathname !== '/notifications' && location.pathname !== '/calendar-invite/') {
            (async () => {
                const result = await getNotificationsData(requestParams);
                if (result?.data?.response) {
                    setNotificationsData(result.data.response.notifications);
                    setUnread(result.data.response.total_unread);
                }
            })();
        }
    }, [getNotificationsData, requestParams, location.pathname]);

    return (
        <header className='h-30 bg-auth bg-center bg-cover rounded-b-4xl z-[1001] fixed w-full'>
            <div className='h-30 max-w-3xl w-full mx-auto flex flex-col justify-end px-4'>
                <div className='flex items-center justify-between mb-4'>
                    {sidebar && <MenuSidebar />}
                    {goBack && (
                        <div className='w-8 h-8 cursor-pointer' onClick={() => backAction()}>
                            <IconArrowLeft className='ml-2 w-8 h-8 stroke-theme1-white' />
                        </div>
                    )}
                    {staticHeader && <div className='w-8 h-8'></div>}
                    <div className='font-bold text-2xl text-theme1-white ml-auto mr-auto text-center'>{title}</div>
                    {location.pathname !== '/notifications' && location.pathname !== '/calendar-invite/' && (
                        <>
                            {goBack && !forwardRoute && <div className='w-8 h-8'></div>}
                            <div
                                className={`${forwardRoute ? 'mr-2' : ''} p-2 relative inline-block cursor-pointer`}
                                onClick={() => setShow(true)}
                            >
                                <IconBell className={'stroke-theme1-white w-6 h-6'} />
                                {unread > 0 && (
                                    <span
                                        className={
                                            'absolute -top-2 -right-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-theme1-brightRed2 px-1 text-[10px] font-bold leading-none text-white'
                                        }
                                    >
                                        {unread}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                    {forwardRoute ? (
                        <>
                            {hasForwardPermission || !forwardPermission ? (
                                <Link to={forwardRoute}>
                                    {ForwardIcon ? (
                                        <ForwardIcon className={twMerge('mr-2 w-8 h-8', forwardIconClassName)} />
                                    ) : (
                                        <IconArrowRight className='mr-2 w-8 h-8 stroke-theme1-white' />
                                    )}
                                </Link>
                            ) : (
                                <div className='w-8 h-8'></div>
                            )}
                        </>
                    ) : (
                        location.pathname === '/notifications' &&
                        location.pathname === '/calendar-invite/' && (
                            <>
                                <div className='w-8 h-8'></div>
                                <div className='w-10 h-10'></div>
                            </>
                        )
                    )}
                </div>
            </div>

            <Dialog
                className='rounded-4xl !w-[500px]'
                visible={show}
                style={{ marginLeft: '15px', marginRight: '15px' }}
                breakpoints={{ '420px': '320px' }}
                onHide={() => setShow(false)}
                content={() => (
                    <div className='flex flex-col justify-center items-center px-6 py-8 pt-12 bg-white rounded-4xl'>
                        <button onClick={() => setShow(false)} className='absolute top-6 right-6'>
                            <IconCloseFull className='fill-theme1-text5' />
                        </button>
                        <div className='text-theme1-title2 w-full mb-[10px] leading-tight'>
                            <div className={'font-bold text-center text-[22px] mb-[10px]'}>Unread notifications</div>

                            {notificationsData.length > 0 &&
                                notificationsData.map((notification) => (
                                    <div key={notification.id} className={'flex py-2'}>
                                        <Link
                                            to={link(notification)}
                                            onClick={() => handleMarkAsRead([notification.id], notification.status)}
                                            className={'block'}
                                        >
                                            <div className='flex rounded-3xl bg-theme1-white shadow-lg border border-theme1-border1 p-[16px]'>
                                                <div className='w-auto'>{renderIcon(notification.type)}</div>
                                                <div className='grid'>
                                                    <span
                                                        className={`font-bold text-theme1-title2 ${
                                                            notification.status === 'unread' ? 'font-extrabold' : ''
                                                        }`}
                                                    >
                                                        {notification.title}
                                                    </span>
                                                    <span className='text-theme1-text1'>{notification.message}</span>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                        </div>
                        {notificationsData.length === 0 && (
                            <div>
                                <div>You have no new notifications!</div>
                            </div>
                        )}
                        <BaseButton onClick={() => navigate('/notifications')} className={'mt-5 btn-red-outline'}>
                            View all
                        </BaseButton>
                    </div>
                )}
            ></Dialog>
        </header>
    );
};

export default Header;
